import { useState, useEffect } from 'react';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FormHelpText from './form-helpText';
import { classNames } from '@repo/ui/src/utils/classNames';

interface EnumOption {
  optionTitle: string;
}

interface FormMultiSelectItem {
  title?: string;
  required?: boolean;
  enumOptions?: Array<EnumOption>;
  validationMessage?: string;
  helpText?: string;
}

interface FormMultiSelectProps {
  item: FormMultiSelectItem;
  unique: string;
  submitted: boolean;
}

const FormMultiSelect = ({ item, unique, submitted }: FormMultiSelectProps) => {
  const [value, setValue] = useState<Array<string>>([]);
  const [error, setError] = useState(false);
  const validate = (val: Array<string>) => {
    if (item.required && val.length === 0) {
      setError(true);
    } else {
      setError(false);
    }
  };

  useEffect(() => {
    if (submitted) {
      validate(value);
    }
  });

  return (
    <>
      <FormGroup className="py-4">
        <InputLabel className="font-normal text-[#42474a] tra-text-web-body-m">{`${
          item.title
        }${item.required ? ' *' : ''}`}</InputLabel>
        <FormControl fullWidth error={error}>
          <Select
            id={`form-${unique}`}
            name={unique}
            title={unique}
            multiple
            value={value}
            variant="outlined"
            required={item.required}
            onBlur={(e) => validate(e.target.value as unknown as Array<string>)}
            onChange={(e) => setValue(e.target.value as Array<string>)}
            inputProps={{
              title: `${item.title}${item.required ? ' *' : ''}`,
            }}
            placeholder="Select At Least One"
            renderValue={(selected) => (selected as Array<string>).join(', ')}
            className={classNames(
              'bg-white',
              error ? 'shadow-formError focus:shadow-formError' : 'focus:shadow-form',
            )}
            endAdornment={
              <InputAdornment className="pr-6" position="end">
                {error ? (
                  <CancelIcon color="error" />
                ) : value.length === 0 ? (
                  ''
                ) : (
                  <CheckCircleIcon color="primary" />
                )}
              </InputAdornment>
            }
          >
            {item.enumOptions
              ? item.enumOptions.map((option) => (
                  <MenuItem
                    key={`option-${unique}-${option.optionTitle
                      .toLowerCase()
                      .replace(/[^\w]/gi, '_')}`}
                    value={option.optionTitle}
                  >
                    <Checkbox checked={value.indexOf(option.optionTitle) > -1} />
                    <ListItemText primary={option.optionTitle} />
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormHelperText className={classNames('mt-1 text-red-400', error ? 'block' : 'hidden')}>
          {item.validationMessage}
        </FormHelperText>
        <FormHelpText
          display={!!item.helpText}
          unique={unique}
          content={item.helpText !== undefined ? item.helpText : ''}
        />
      </FormGroup>
    </>
  );
};

export default FormMultiSelect;
